
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

export const useTagProposals = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['tag-proposals', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('tag_proposals')
        .select('*')
        .eq('proposed_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};

export const useAllTagProposals = () => {
  return useQuery({
    queryKey: ['all-tag-proposals'],
    queryFn: async () => {
      // Récupérer d'abord les tag proposals
      const { data: proposals, error: proposalsError } = await supabase
        .from('tag_proposals')
        .select('*')
        .order('created_at', { ascending: false });

      if (proposalsError) {
        console.error('Error fetching tag proposals:', proposalsError);
        throw proposalsError;
      }

      // Récupérer les profils des utilisateurs qui ont proposé des tags
      const userIds = proposals?.map(p => p.proposed_by).filter(Boolean) || [];
      
      if (userIds.length === 0) {
        return proposals?.map(p => ({ ...p, profiles: null })) || [];
      }

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, email')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        // Continuer sans les profils si erreur
        return proposals?.map(p => ({ ...p, profiles: null })) || [];
      }

      // Associer les profils aux propositions
      const proposalsWithProfiles = proposals?.map(proposal => {
        const profile = profiles?.find(p => p.id === proposal.proposed_by);
        return {
          ...proposal,
          profiles: profile || null
        };
      }) || [];

      return proposalsWithProfiles;
    },
  });
};

export const useCreateTagProposal = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (proposal: {
      name: string;
      description?: string;
      color?: string;
    }) => {
      if (!user) throw new Error('Authentication required');

      const { data, error } = await supabase
        .from('tag_proposals')
        .insert({
          ...proposal,
          proposed_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tag-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['all-tag-proposals'] });
      
      toast({
        title: 'Proposition envoyée !',
        description: 'Votre proposition de tag a été envoyée et sera examinée par un administrateur.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de créer la proposition de tag.',
        variant: 'destructive',
      });
    },
  });
};

export const useApproveTagProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ proposalId, adminNotes }: { proposalId: string; adminNotes?: string }) => {
      const { error } = await supabase.rpc('approve_tag_proposal', {
        proposal_id: proposalId,
        admin_notes_param: adminNotes,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-tag-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['server-tags'] });
      
      toast({
        title: 'Tag approuvé !',
        description: 'Le tag a été créé et ajouté à la liste des tags disponibles.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'approuver le tag.',
        variant: 'destructive',
      });
    },
  });
};

export const useRejectTagProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ proposalId, adminNotes }: { proposalId: string; adminNotes: string }) => {
      //console.log('Rejecting proposal with params:', { proposalId, adminNotes });
      
      const { error } = await supabase.rpc('reject_tag_proposal', {
        proposal_id: proposalId,
        admin_notes_param: adminNotes,
      });

      if (error) {
        console.error('Error rejecting proposal:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-tag-proposals'] });
      
      toast({
        title: 'Proposition rejetée',
        description: 'La proposition de tag a été rejetée.',
        variant: 'destructive',
      });
    },
    onError: (error: any) => {
      console.error('Reject proposal error:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de rejeter la proposition.',
        variant: 'destructive',
      });
    },
  });
};
